import { useQuery } from '@apollo/client';
import moment from 'moment';
import React, { useState } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import { CardSeparator } from '../components/Card';
import Caret from '../components/Caret';
import Timestamp from '../components/Timestamp';
import PopOver from '../components/deprecated/PopOver';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import { PostType } from '../graphql/generated/graphql';
import { Link } from '../utils/router/v3Bridge';
import { createPostPath } from '../utils/url/postPath';
import userPath from '../utils/url/userPath';
import yearSensitiveDateString from '../utils/yearSensitiveDateString';
import PostStateBarOverflowMenu from './PostStateBarOverflowMenu';
import styles from './PostVerifiedBar.module.scss';
import PostViewStateBar from './PostViewStateBar';
import VerificationEntries from './VerificationEntries';
import VerifiedIcon from './VerifiedIcon';
import VerifyMenuItems, {
  useShouldRenderVerifyMenuItems,
} from './VerifyMenuItems';
import { isVerifier } from './postPolicyCheck';
import { calculateExpirationTimestamp } from './verificationDateHelpers';
import useStartReverification from './useStartReverification';
import VerifyHistoryMenuItem from './VerifyHistoryMenuItem';
import { gql } from '../graphql/generated/gql';
import type { PostStateBarComponentProps } from './PostStateBar';

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const POST_QUERY = gql(`
  query PostVerifiedBar($id: ID!) {
    post(id: $id) {
      id
      type
      verification {
        id
        unit
        quantity
      }
      verificationEntries {
        id
        verifiedAt
        verifier {
          id
          name
          title
          ...UserAvatar
        }
        ...VerificationEntries_verificationEntries
      }
      ...isVerifier_post
      ...VerifyMenuItems_post
      ...VerifyHistoryMenuItem_post
      ...useStartReverification_post
    }
  }
`);

const PostVerifiedBar = ({ postId, compact }: PostStateBarComponentProps) => {
  const shouldRenderVerifyMenuItems = useShouldRenderVerifyMenuItems();
  const user = useAppSelector((state) => state.session.user);
  const [isExpanded, setIsExpanded] = useState(false);
  const { data } = useQuery(POST_QUERY, { variables: { id: postId } });
  const post = data?.post;
  const { loading, startReverification } = useStartReverification(post);

  if (!post) return null;

  const [currentVerificationEntry, ...previousVerificationEntries] =
    post.verificationEntries;

  if (!currentVerificationEntry || !post.verification) return null;

  const { verifier, verifiedAt } = currentVerificationEntry;
  const expirationTimestamp = calculateExpirationTimestamp(verifiedAt, {
    unit: post.verification.unit,
    quantity: post.verification.quantity,
  }).unsafe();

  const isVerificationAlmostExpiredAndCanVerify =
    moment(expirationTimestamp) <= moment().add(1, 'week') &&
    isVerifier(post, user);

  const isTemplate = post.type === PostType.Template;

  return (
    <PostViewStateBar
      actions={
        isVerificationAlmostExpiredAndCanVerify ? (
          <Button
            variant="secondary"
            size="small"
            onClick={startReverification}
            disabled={loading}
          >
            Reverify
          </Button>
        ) : (
          <div className={styles.actions}>
            {shouldRenderVerifyMenuItems({ post, user }) && (
              <PostStateBarOverflowMenu variant="success">
                <VerifyMenuItems post={post} />
                <VerifyHistoryMenuItem post={post} />
              </PostStateBarOverflowMenu>
            )}
            {isTemplate && (
              <Button
                variant="primary"
                to={createPostPath({ templateId: post.id })}
                size="small"
              >
                Create post
              </Button>
            )}
          </div>
        )
      }
    >
      <VerifiedIcon />
      {/* @ts-expect-error */}
      <PopOver
        display="inline-block"
        popOverPortal={document.body}
        placement="bottom-start"
        popOver={
          <PopOverContainer width="wide">
            <Link to={userPath(verifier)} className={styles.verifierContainer}>
              <Avatar user={verifier} />
              <div className={styles.verifierInfo}>
                <div className={styles.verifierName}>{verifier.name}</div>
                {verifier.title && <div>{verifier.title}</div>}
              </div>
            </Link>
            {previousVerificationEntries.length > 0 && (
              <>
                <CardSeparator />
                <Button
                  className={styles.previousHeader}
                  onClick={(e) => {
                    setIsExpanded(!isExpanded);
                    e.stopPropagation();
                  }}
                >
                  Previous verifications
                  <Caret direction={isExpanded ? 'up' : 'down'} size="small" />
                </Button>
                {isExpanded && (
                  <div className={styles.verificationEntries}>
                    <VerificationEntries
                      verificationEntries={previousVerificationEntries}
                    />
                  </div>
                )}
              </>
            )}
          </PopOverContainer>
        }
        trigger="hover"
      >
        {isVerificationAlmostExpiredAndCanVerify ? (
          <>
            <strong>Verification</strong>
            {' expires '}
            {moment(expirationTimestamp).diff(moment(), 'days') >= 1 ? (
              <Timestamp date={expirationTimestamp} />
            ) : (
              'today'
            )}
            .{!compact && ' Review and reverify to retain its status.'}
          </>
        ) : (
          <>
            <strong>{isTemplate ? 'Template Verified' : 'Verified'}</strong>
            {!compact && (
              <>
                {' '}
                by{' '}
                <Link className={styles.verifier} to={userPath(verifier)}>
                  {verifier.name}
                </Link>{' '}
                on {yearSensitiveDateString(verifiedAt)}
              </>
            )}
          </>
        )}
      </PopOver>
    </PostViewStateBar>
  );
};

export default PostVerifiedBar;
